"use client";

import { ArrowUpRight, ShieldCheck, Zap, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function VpnLatencyAnalytics() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pillars = [{
    number: "01",
    icon: Zap,
    titleTop: "REAL-TIME",
    titleBottom: "LATENCY",
    description: "Monitor connection speeds across global nodes. Instant data streams ensure your traffic flows through the fastest paths.",
    offset: "lg:translate-y-0"
  }, {
    number: "02",
    icon: ShieldCheck,
    titleTop: "THREAT",
    titleBottom: "DETECTION",
    description: "Proactive security scanning for every packet. Identify malicious patterns before they reach your local network environment.",
    offset: "lg:-translate-y-6"
  }, {
    number: "03",
    icon: Lock,
    titleTop: "ENCRYPTED",
    titleBottom: "TUNNELS",
    description: "Military-grade protocols shielding your identity. Data remains private, secure, and invisible to external monitoring eyes.",
    offset: "lg:translate-y-0"
  }];
  return <section data-section-id="3539" id="vpnlatencyanalytics" className="relative bg-background text-foreground overflow-hidden py-24 lg:py-32">
      {}
      <div className="pointer-events-none absolute -top-40 -right-40 h-[600px] w-[600px] opacity-30" style={{
      background: "radial-gradient(circle, hsla(22, 100%, 50%, 0.25) 0%, transparent 70%)"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        {}
        <div ref={headerRef} className={`max-w-3xl transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-mono text-xs tracking-widest text-primary mb-8 uppercase">
            [ 03 / ANALYTICS ]
          </p>
          <h2 className="font-default font-bold text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tighter text-foreground uppercase">
            Latency
            <br />
            <span className="text-primary">Analytics</span>
          </h2>
          <p className="font-serif text-xl lg:text-2xl text-muted-foreground mt-8 leading-relaxed max-w-xl">
            Three pillars. One secure connection.
          </p>
        </div>

        {}
        <div ref={cardsRef} className="mt-24 lg:mt-32 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-0 relative">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <div key={i} data-index={i} className={`group relative transition-all duration-1000 ease-out ${pillar.offset} ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                {}
                {i > 0 && <div className="hidden lg:block absolute left-0 top-12 bottom-12 w-px bg-border" aria-hidden="true" />}

                <div className="relative h-full min-h-[480px] flex flex-col p-10 lg:p-12 border border-border transition-all duration-500 ease-out group-hover:-translate-y-3 group-hover:border-primary/50" style={{
              background: "var(--card)",
              boxShadow: "var(--shadow-lg)"
            }}>
                  {}
                  <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{
                background: "radial-gradient(circle at 50% 50%, hsla(22, 100%, 50%, 0.1) 0%, transparent 70%)"
              }} aria-hidden="true" />

                  {}
                  <div className="relative z-10 flex items-center justify-between">
                    <span className="font-mono text-xs tracking-widest text-primary uppercase">
                      [ {pillar.number} ]
                    </span>
                    <span className="font-mono text-xs tracking-widest text-muted-foreground uppercase">
                      / DATA
                    </span>
                  </div>

                  {}
                  <div className="relative z-10 flex-1 flex items-center justify-center my-8">
                    <div className="relative w-48 h-48 lg:w-56 lg:h-56 flex items-center justify-center">
                      {}
                      <div className="absolute inset-0 border border-border transition-all duration-500 group-hover:border-primary/40 group-hover:scale-110" aria-hidden="true" />
                      <div className="absolute inset-4 border border-border transition-all duration-700 group-hover:border-primary/20 group-hover:rotate-45" aria-hidden="true" />
                      <Icon className="relative w-24 h-24 lg:w-28 lg:h-28 text-foreground/80 transition-all duration-500 group-hover:text-primary group-hover:scale-110" strokeWidth={1} />
                    </div>
                  </div>

                  {}
                  <h3 className="relative z-10 font-default font-bold text-3xl lg:text-4xl leading-[0.95] tracking-tighter uppercase text-foreground">
                    {pillar.titleTop}
                    <br />
                    <span className="text-foreground/60 group-hover:text-primary transition-colors duration-500">
                      {pillar.titleBottom}
                    </span>
                  </h3>

                  {}
                  <p className="relative z-10 font-serif text-base lg:text-lg leading-relaxed text-muted-foreground mt-6">
                    {pillar.description}
                  </p>

                  {}
                  <div className="relative z-10 mt-8 pt-6 border-t border-border">
                    <Button variant="ghost" className="group/btn h-auto p-0 font-mono text-xs tracking-widest uppercase text-foreground hover:bg-transparent hover:text-primary">
                      <span className="relative inline-block">
                        View Details
                        <span className="absolute -bottom-1 left-0 h-px w-0 bg-primary transition-all duration-500 group-hover/btn:w-full" aria-hidden="true" />
                      </span>
                      <ArrowUpRight className="ml-2 h-4 w-4 transition-transform duration-500 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
                    </Button>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}